import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  DonateWidget,
  DonateWidgetGridBreakpoint,
  DonateWidgetGridLayoutItem,
  type DonationAlertEvent,
  donateErrorMessage,
  donateWidgetPublicGetOne,
  getDonationAlertEventsUrl,
  normalizeDonateApiUrl,
} from "services/main/donate.service";
import "./widget-public.scss";

type WidgetKind =
  | "alert"
  | "goal"
  | "leaderboard"
  | "timer"
  | "chat"
  | "text"
  | "image"
  | "amount";

type WidgetAnchor = "free" | "center" | "top" | "bottom" | "left" | "right";
type AlertLayoutPreset =
  | "icon-top"
  | "center"
  | "icon-left"
  | "icon-right"
  | "banner"
  | "compact"
  | "text-only";

export type PublicGridItem = {
  id?: string;
  type?: string;
  title?: string;
  isVisible?: boolean;
  data?: Record<string, any>;
  settings?: Record<string, any>;
  styles?: Record<string, any>;
};

type AlertEvent = {
  id?: string;
  _id?: string;
  transactionId?: string;
  donationId?: string;
  name?: string;
  donorName?: string;
  donor?: string;
  amount?: number | string;
  currency?: string;
  message?: string;
  paymentMethod?: string;
  ttsAudioUrl?: string;
  notiAudioUrl?: string;
  displayCount?: number | string;
  lastDisplayedAt?: string;
  _receivedAt?: number;
} & DonationAlertEvent;

type TestAlertFormState = {
  name: string;
  amount: string;
  paymentMethod: string;
  message: string;
};

const DEFAULT_BREAKPOINTS: Record<DonateWidgetGridBreakpoint, number> = {
  lg: 1200,
  md: 996,
  sm: 768,
  xs: 480,
  xxs: 0,
};

const DEFAULT_COLS: Record<DonateWidgetGridBreakpoint, number> = {
  lg: 24,
  md: 20,
  sm: 12,
  xs: 8,
  xxs: 4,
};

const BREAKPOINTS: DonateWidgetGridBreakpoint[] = [
  "lg",
  "md",
  "sm",
  "xs",
  "xxs",
];

const DEFAULT_ROW_HEIGHT = 48;
const DEFAULT_MARGIN: [number, number] = [8, 8];
const DEFAULT_CONTAINER_PADDING: [number, number] = [0, 0];
const DEFAULT_WIDGET_IMAGE = "/images/profile.png";
const DEFAULT_ACCENT_COLOR = "#168FEB";
const BOTTOM_LEFT_ANCHOR_THRESHOLD_ROWS = 1;
const ALERT_AUDIO_RETRY_COUNT = 1;
const ALERT_AUDIO_RETRY_DELAY_MS = 300;
const ALERT_AUDIO_ATTEMPT_TIMEOUT_MS = 60000;
const ALERT_LAYOUT_PRESETS: AlertLayoutPreset[] = [
  "icon-top",
  "center",
  "icon-left",
  "icon-right",
  "banner",
  "compact",
  "text-only",
];
const activeAlertAudioStops = new Set<() => void>();

const stopActiveAlertAudio = () => {
  Array.from(activeAlertAudioStops).forEach((stop) => stop());
  activeAlertAudioStops.clear();
};

const previewLeaderboardRows = [
  { name: "nova_plays86", amount: 389559 },
  { name: "jojo_stream", amount: 275000 },
  { name: "rex_mp3", amount: 188000 },
];

const previewChatRows = [
  { user: "Bibilie", message: "Nice stream!", color: "#168FEB" },
  { user: "Nova", message: "Keep going", color: "#16A34A" },
  { user: "Rex", message: "GG", color: "#F59E0B" },
];

const normalizeWidgetResponse = (response: any): DonateWidget =>
  (response?.data || response || {}) as DonateWidget;

const normalizeNumber = (value: unknown, fallback = 0) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : fallback;
};

const normalizeString = (value: unknown, fallback = "") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
};

const normalizeBoolean = (value: unknown, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
};

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

const normalizeWidgetAnchor = (value: unknown): WidgetAnchor => {
  if (
    value === "center" ||
    value === "top" ||
    value === "bottom" ||
    value === "left" ||
    value === "right"
  ) {
    return value;
  }
  if (value === "bottom-left") return "bottom";
  return "free";
};

const normalizeColor = (value: unknown, fallback = DEFAULT_ACCENT_COLOR) => {
  const color = normalizeString(value, fallback);
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color) ? color : fallback;
};

const hexToRgba = (color: string, alpha: number) => {
  const normalized = normalizeColor(color).replace("#", "");
  const hex =
    normalized.length === 3
      ? normalized
          .split("")
          .map((part) => `${part}${part}`)
          .join("")
      : normalized;
  const value = Number.parseInt(hex, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getWidgetNodeStyle = (
  color: string,
  extra?: React.CSSProperties,
): React.CSSProperties =>
  ({
    "--widget-accent": normalizeColor(color),
    "--widget-accent-soft": hexToRgba(color, 0.26),
    ...extra,
  }) as React.CSSProperties;

const getAnimationClass = (animation: unknown) => {
  if (animation === "slide") return "donation-widget-slide";
  if (animation === "fade") return "donation-widget-fade";
  if (animation === "bounce") return "donation-widget-bounce";
  return "donation-widget-pop";
};

const normalizeChoice = (
  value: unknown,
  choices: string[],
  fallback: string,
) => {
  const nextValue = normalizeString(value, fallback);
  return choices.includes(nextValue) ? nextValue : fallback;
};

const isAlertLayoutPreset = (value: unknown): value is AlertLayoutPreset =>
  ALERT_LAYOUT_PRESETS.includes(value as AlertLayoutPreset);

const getAlertLayoutPresetFromTheme = (theme: unknown): AlertLayoutPreset => {
  if (theme === "minimal") return "icon-left";
  if (theme === "marquee") return "text-only";
  if (theme === "banner") return "banner";
  if (theme === "compact") return "compact";
  return "icon-top";
};

const normalizeAlertLayoutPreset = (
  value: unknown,
  fallbackTheme?: unknown,
): AlertLayoutPreset =>
  isAlertLayoutPreset(value)
    ? value
    : getAlertLayoutPresetFromTheme(fallbackTheme);

const getAlertLayoutBaseSize = (
  layoutPreset: AlertLayoutPreset,
  showIcon: boolean,
) => {
  if (layoutPreset === "compact") {
    return { width: showIcon ? 280 : 230, height: 68 };
  }

  if (layoutPreset === "text-only") {
    return { width: 460, height: 108 };
  }

  if (layoutPreset === "banner") {
    return { width: 540, height: 148 };
  }

  if (layoutPreset === "icon-left" || layoutPreset === "icon-right") {
    return { width: showIcon ? 380 : 300, height: 136 };
  }

  if (layoutPreset === "center") {
    return { width: 340, height: showIcon ? 260 : 190 };
  }

  return { width: 320, height: showIcon ? 204 : 150 };
};

const normalizeTuple = (
  value: unknown,
  fallback: [number, number],
): [number, number] => {
  if (!Array.isArray(value)) return fallback;

  return [
    normalizeNumber(value[0], fallback[0]),
    normalizeNumber(value[1], fallback[1]),
  ];
};

const formatCurrency = (amount: unknown) => {
  const value = normalizeNumber(amount, 0);
  return new Intl.NumberFormat("lo-LA").format(value);
};

const formatTimer = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const rest = safeSeconds % 60;

  return [hours, minutes, rest]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

const isWidgetKind = (value: unknown): value is WidgetKind =>
  value === "alert" ||
  value === "goal" ||
  value === "leaderboard" ||
  value === "timer" ||
  value === "chat" ||
  value === "text" ||
  value === "image" ||
  value === "amount";

const getKindFromType = (type?: string): WidgetKind => {
  if (type === "DONATION_ALERT") return "alert";
  if (type === "DONATION_GOAL") return "goal";
  if (type === "DONATION_LIST") return "leaderboard";
  return "text";
};

const getWidgetKind = (widget?: DonateWidget): WidgetKind => {
  const widgetKey = widget?.metadata?.widgetKey;
  if (isWidgetKind(widgetKey)) return widgetKey;
  return getKindFromType(widget?.type);
};

const getItemKind = (
  item: PublicGridItem,
  widget?: DonateWidget,
): WidgetKind => {
  const kind = item.data?.kind || item.settings?.kind || item.type;
  if (isWidgetKind(kind)) return kind;
  return getKindFromType(String(item.type || widget?.type || ""));
};

const getItemId = (item: PublicGridItem, index: number) =>
  String(item.id || item.data?.id || `node-${index + 1}`);

const getBreakpoint = (
  width: number,
  breakpoints?: Partial<Record<DonateWidgetGridBreakpoint, number>>,
) => {
  const nextBreakpoints = { ...DEFAULT_BREAKPOINTS, ...(breakpoints || {}) };

  return BREAKPOINTS.find((key) => width >= nextBreakpoints[key]) || "xxs";
};

const getGridRowCount = ({
  viewportHeight,
  rowHeight,
  marginY,
  containerPaddingY,
}: {
  viewportHeight: number;
  rowHeight: number;
  marginY: number;
  containerPaddingY: number;
}) => {
  const innerHeight = Math.max(viewportHeight - containerPaddingY * 2, 1);
  return Math.max(
    Math.round((innerHeight + marginY) / (rowHeight + marginY)),
    1,
  );
};

const getFittedRowHeight = ({
  viewportHeight,
  rowHeight,
  marginY,
  containerPaddingY,
}: {
  viewportHeight: number;
  rowHeight: number;
  marginY: number;
  containerPaddingY: number;
}) => {
  const rows = getGridRowCount({
    viewportHeight,
    rowHeight,
    marginY,
    containerPaddingY,
  });
  const innerHeight = Math.max(
    viewportHeight - containerPaddingY * 2 - marginY * Math.max(rows - 1, 0),
    rows,
  );

  return innerHeight / rows;
};

const getGridConfig = (
  widget: DonateWidget,
  breakpoint: DonateWidgetGridBreakpoint,
  viewportWidth: number,
  viewportHeight?: number,
) => {
  const grid = widget.gridLayout;
  const cols = normalizeNumber(
    grid?.cols?.[breakpoint],
    DEFAULT_COLS[breakpoint],
  );
  const idealRowHeight = normalizeNumber(grid?.rowHeight, DEFAULT_ROW_HEIGHT);
  const margin = normalizeTuple(grid?.margin, DEFAULT_MARGIN);
  const containerPadding = normalizeTuple(
    grid?.containerPadding,
    DEFAULT_CONTAINER_PADDING,
  );
  const rowHeight =
    typeof viewportHeight === "number"
      ? getFittedRowHeight({
          viewportHeight,
          rowHeight: idealRowHeight,
          marginY: margin[1],
          containerPaddingY: containerPadding[1],
        })
      : idealRowHeight;
  const safeCols = Math.max(cols, 1);
  const innerWidth = Math.max(
    viewportWidth - containerPadding[0] * 2 - margin[0] * (safeCols - 1),
    safeCols,
  );

  return {
    cols: safeCols,
    rowHeight,
    margin,
    containerPadding,
    colWidth: innerWidth / safeCols,
  };
};

const getGridRows = (
  widget: DonateWidget,
  breakpoint: DonateWidgetGridBreakpoint,
  viewportWidth: number,
  viewportHeight: number,
) => {
  const config = getGridConfig(widget, breakpoint, viewportWidth);
  return Math.max(
    getGridRowCount({
      viewportHeight,
      rowHeight: config.rowHeight,
      marginY: config.margin[1],
      containerPaddingY: config.containerPadding[1],
    }),
    1,
  );
};

const getDefaultSize = (
  kind: WidgetKind,
  cols: number,
  breakpoint: DonateWidgetGridBreakpoint,
  alertLayoutPreset?: AlertLayoutPreset,
) => {
  const isCompact = breakpoint === "xs" || breakpoint === "xxs";
  const alertSizeByPreset: Record<AlertLayoutPreset, { w: number; h: number }> =
    {
      compact: { w: Math.min(cols, isCompact ? cols : 3), h: 1 },
      "text-only": { w: Math.min(cols, isCompact ? cols : 6), h: 2 },
      banner: { w: Math.min(cols, isCompact ? cols : 7), h: 2 },
      "icon-left": { w: Math.min(cols, isCompact ? cols : 5), h: 2 },
      "icon-right": { w: Math.min(cols, isCompact ? cols : 5), h: 2 },
      center: { w: Math.min(cols, isCompact ? cols : 5), h: 4 },
      "icon-top": { w: Math.min(cols, isCompact ? cols : 4), h: 3 },
    };
  const sizeByKind: Record<WidgetKind, { w: number; h: number }> = {
    alert: alertSizeByPreset[alertLayoutPreset || "icon-top"],
    goal: { w: Math.min(cols, isCompact ? cols : 5), h: 2 },
    leaderboard: { w: Math.min(cols, isCompact ? cols : 4), h: 3 },
    timer: { w: Math.min(cols, isCompact ? 4 : 3), h: 2 },
    chat: { w: Math.min(cols, isCompact ? cols : 4), h: 4 },
    text: { w: Math.min(cols, isCompact ? cols : 3), h: 1 },
    image: { w: Math.min(cols, isCompact ? 4 : 2), h: 2 },
    amount: { w: Math.min(cols, isCompact ? 4 : 2), h: 1 },
  };

  return sizeByKind[kind] || sizeByKind.text;
};

const getFrontendPlacement = (item: PublicGridItem) =>
  item.settings?.frontendPlacement ||
  item.data?.frontendPlacement ||
  item.settings?.placement ||
  item.data?.placement;

const getResponsivePlacementLayout = ({
  placement,
  itemId,
  kind,
  breakpoint,
  cols,
  rows,
  alertLayoutPreset,
}: {
  placement: any;
  itemId: string;
  kind: WidgetKind;
  breakpoint: DonateWidgetGridBreakpoint;
  cols: number;
  rows: number;
  alertLayoutPreset?: AlertLayoutPreset;
}): DonateWidgetGridLayoutItem | undefined => {
  if (!placement || typeof placement !== "object") return undefined;

  const fallbackSize = getDefaultSize(
    kind,
    cols,
    breakpoint,
    alertLayoutPreset,
  );
  const sourceCols = Math.max(normalizeNumber(placement.cols, cols), 1);
  const sourceRows = Math.max(normalizeNumber(placement.rows, rows), 1);
  const sourceW = Math.max(normalizeNumber(placement.w, fallbackSize.w), 1);
  const sourceH = Math.max(normalizeNumber(placement.h, fallbackSize.h), 1);
  const w = clampNumber(
    Math.max(Math.round((sourceW / sourceCols) * cols), fallbackSize.w),
    1,
    cols,
  );
  const h = clampNumber(
    Math.max(Math.round((sourceH / sourceRows) * rows), fallbackSize.h),
    1,
    rows,
  );

  return {
    i: itemId,
    x: clampNumber(
      Math.round((normalizeNumber(placement.x, 0) / sourceCols) * cols),
      0,
      cols - w,
    ),
    y: clampNumber(
      Math.round((normalizeNumber(placement.y, 0) / sourceRows) * rows),
      0,
      rows - h,
    ),
    w,
    h,
  };
};

const getFrontendLayout = ({
  widget,
  items,
  item,
  index,
  breakpoint,
  cols,
  viewportWidth,
  viewportHeight,
}: {
  widget: DonateWidget;
  items: PublicGridItem[];
  item: PublicGridItem;
  index: number;
  breakpoint: DonateWidgetGridBreakpoint;
  cols: number;
  viewportWidth: number;
  viewportHeight: number;
}): DonateWidgetGridLayoutItem => {
  const rows = getGridRows(widget, breakpoint, viewportWidth, viewportHeight);
  const kind = getItemKind(item, widget);
  const settings = getMergedSettings(widget, item);
  const alertLayoutPreset =
    kind === "alert"
      ? normalizeAlertLayoutPreset(
          settings.layoutPreset || settings.layout,
          settings.theme,
        )
      : undefined;
  const size = getDefaultSize(kind, cols, breakpoint, alertLayoutPreset);
  const anchor = getItemAnchor(widget, item);
  const itemId = getItemId(item, index);
  const responsivePlacement = getResponsivePlacementLayout({
    placement: getFrontendPlacement(item),
    itemId,
    kind,
    breakpoint,
    cols,
    rows,
    alertLayoutPreset,
  });
  const w = clampNumber(responsivePlacement?.w || size.w, 1, cols);
  const h = clampNumber(responsivePlacement?.h || size.h, 1, rows);

  if (anchor === "center") {
    return {
      i: itemId,
      x: Math.max(Math.floor((cols - w) / 2), 0),
      y: Math.max(Math.floor((rows - h) / 2), 0),
      w,
      h,
    };
  }

  if (anchor === "top") {
    return {
      i: itemId,
      x: Math.max(Math.floor((cols - w) / 2), 0),
      y: 0,
      w,
      h,
    };
  }

  if (anchor === "bottom") {
    return {
      i: itemId,
      x: Math.max(Math.floor((cols - w) / 2), 0),
      y: Math.max(rows - h, 0),
      w,
      h,
    };
  }

  if (anchor === "left") {
    return {
      i: itemId,
      x: 0,
      y: Math.max(Math.floor((rows - h) / 2), 0),
      w,
      h,
    };
  }

  if (anchor === "right") {
    return {
      i: itemId,
      x: Math.max(cols - w, 0),
      y: Math.max(Math.floor((rows - h) / 2), 0),
      w,
      h,
    };
  }

  if (responsivePlacement) return responsivePlacement;

  if (items.length === 1) {
    return {
      i: itemId,
      x: Math.max(Math.floor((cols - w) / 2), 0),
      y: Math.max(Math.floor((rows - h) / 2), 0),
      w,
      h,
    };
  }

  const gap = 1;
  const startY = breakpoint === "lg" || breakpoint === "md" ? 2 : 1;
  const y =
    startY +
    items.slice(0, index).reduce((total, previousItem) => {
      const previousKind = getItemKind(previousItem, widget);
      const previousSettings = getMergedSettings(widget, previousItem);
      const previousAlertLayoutPreset =
        previousKind === "alert"
          ? normalizeAlertLayoutPreset(
              previousSettings.layoutPreset || previousSettings.layout,
              previousSettings.theme,
            )
          : undefined;
      const previousSize = getDefaultSize(
        previousKind,
        cols,
        breakpoint,
        previousAlertLayoutPreset,
      );

      return total + previousSize.h + gap;
    }, 0);

  return {
    i: itemId,
    x: Math.max(Math.floor((cols - w) / 2), 0),
    y,
    w,
    h,
  };
};

const getItemLayout = ({
  widget,
  items,
  item,
  index,
  breakpoint,
  cols,
  viewportWidth,
  viewportHeight,
}: {
  widget: DonateWidget;
  items: PublicGridItem[];
  item: PublicGridItem;
  index: number;
  breakpoint: DonateWidgetGridBreakpoint;
  cols: number;
  viewportWidth: number;
  viewportHeight: number;
}): DonateWidgetGridLayoutItem => {
  const itemId = getItemId(item, index);
  const layouts = widget.gridLayout?.layouts || {};
  const rows = getGridRows(widget, breakpoint, viewportWidth, viewportHeight);
  const clampLayout = (
    layout: DonateWidgetGridLayoutItem,
  ): DonateWidgetGridLayoutItem => {
    const w = clampNumber(Math.round(normalizeNumber(layout.w, 1)), 1, cols);
    const h = clampNumber(Math.round(normalizeNumber(layout.h, 1)), 1, rows);

    return {
      ...layout,
      x: clampNumber(Math.round(normalizeNumber(layout.x, 0)), 0, cols - w),
      y: clampNumber(Math.round(normalizeNumber(layout.y, 0)), 0, rows - h),
      w,
      h,
    };
  };
  const directLayout = layouts[breakpoint]?.find(
    (layout) => String(layout.i) === itemId,
  );

  if (directLayout) return clampLayout(directLayout);

  const fallbackLayout = BREAKPOINTS.flatMap((key) => layouts[key] || []).find(
    (layout) => String(layout.i) === itemId,
  );

  if (fallbackLayout) return clampLayout(fallbackLayout);

  return clampLayout(
    getFrontendLayout({
      widget,
      items,
      item,
      index,
      breakpoint,
      cols,
      viewportWidth,
      viewportHeight,
    }),
  );
};

const getLayoutPixels = ({
  layout,
  widget,
  breakpoint,
  viewportWidth,
  viewportHeight,
}: {
  layout: DonateWidgetGridLayoutItem;
  widget: DonateWidget;
  breakpoint: DonateWidgetGridBreakpoint;
  viewportWidth: number;
  viewportHeight: number;
}) => {
  const config = getGridConfig(
    widget,
    breakpoint,
    viewportWidth,
    viewportHeight,
  );
  const width =
    layout.w * config.colWidth + Math.max(layout.w - 1, 0) * config.margin[0];
  const height =
    layout.h * config.rowHeight + Math.max(layout.h - 1, 0) * config.margin[1];

  return {
    left:
      config.containerPadding[0] +
      layout.x * (config.colWidth + config.margin[0]),
    top:
      config.containerPadding[1] +
      layout.y * (config.rowHeight + config.margin[1]),
    width,
    height,
  };
};

const getWidgetItems = (widget: DonateWidget): PublicGridItem[] => {
  const items = widget.gridLayout?.items || [];
  if (items.length) return items.filter((item) => item?.isVisible !== false);

  const kind = getWidgetKind(widget);
  return [
    {
      id: "default",
      type: widget.type,
      title: widget.name || "Widget",
      data: { kind },
      settings: widget.settings || {},
      styles: widget.styles || {},
    },
  ];
};

const getMergedSettings = (widget: DonateWidget, item: PublicGridItem) => ({
  ...(widget.settings || {}),
  ...(item.data || {}),
  ...(item.settings || {}),
});

const getMergedStyles = (widget: DonateWidget, item: PublicGridItem) => ({
  ...(widget.styles || {}),
  ...(item.styles || {}),
});

const getItemAnchor = (widget: DonateWidget, item: PublicGridItem) =>
  normalizeWidgetAnchor(
    item.settings?.anchor ||
      item.data?.anchor ||
      item.styles?.anchor ||
      widget.settings?.anchor ||
      widget.metadata?.anchor,
  );

const isBottomLeftLayout = ({
  layout,
  widget,
  breakpoint,
  viewportWidth,
  viewportHeight,
}: {
  layout: DonateWidgetGridLayoutItem;
  widget: DonateWidget;
  breakpoint: DonateWidgetGridBreakpoint;
  viewportWidth: number;
  viewportHeight: number;
}) => {
  const rows = getGridRows(widget, breakpoint, viewportWidth, viewportHeight);
  const threshold = Math.max(BOTTOM_LEFT_ANCHOR_THRESHOLD_ROWS, 0);

  return layout.x <= 0 && rows - (layout.y + layout.h) <= threshold;
};

const getPositionedWidgetStyle = ({
  anchor,
  inferredBottomLeft,
  pixels,
  widget,
  breakpoint,
  viewportWidth,
  viewportHeight,
}: {
  anchor: WidgetAnchor;
  inferredBottomLeft: boolean;
  pixels: ReturnType<typeof getLayoutPixels>;
  widget: DonateWidget;
  breakpoint: DonateWidgetGridBreakpoint;
  viewportWidth: number;
  viewportHeight: number;
}): React.CSSProperties => {
  const config = getGridConfig(widget, breakpoint, viewportWidth);
  const width = clampNumber(pixels.width, 1, Math.max(viewportWidth, 1));
  const height = clampNumber(pixels.height, 1, Math.max(viewportHeight, 1));
  const left = clampNumber(pixels.left, 0, viewportWidth - width);
  const top = clampNumber(pixels.top, 0, viewportHeight - height);
  const maxBottom = Math.max(viewportHeight - height, 0);
  const anchoredLeft = clampNumber(
    config.containerPadding[0],
    0,
    viewportWidth - width,
  );
  const anchoredBottom = clampNumber(config.containerPadding[1], 0, maxBottom);
  const anchoredRight = anchoredLeft;
  const anchoredTop = anchoredBottom;
  const centeredLeft = Math.max((viewportWidth - width) / 2, 0);
  const centeredTop = Math.max((viewportHeight - height) / 2, 0);

  if (anchor === "center") {
    return {
      position: "fixed",
      left: centeredLeft,
      top: centeredTop,
      width,
      height,
      maxWidth: "100vw",
      maxHeight: "100vh",
    };
  }

  if (anchor === "top") {
    return {
      position: "fixed",
      left: centeredLeft,
      top: anchoredTop,
      width,
      height,
      maxWidth: "100vw",
      maxHeight: "100vh",
    };
  }

  if (anchor === "bottom" || inferredBottomLeft) {
    return {
      position: "fixed",
      left: centeredLeft,
      bottom: anchoredBottom,
      width,
      height,
      maxWidth: "100vw",
      maxHeight: "100vh",
    };
  }

  if (anchor === "left") {
    return {
      position: "fixed",
      left: anchoredLeft,
      top: centeredTop,
      width,
      height,
      maxWidth: "100vw",
      maxHeight: "100vh",
    };
  }

  if (anchor === "right") {
    return {
      position: "fixed",
      right: anchoredRight,
      top: centeredTop,
      width,
      height,
      maxWidth: "100vw",
      maxHeight: "100vh",
    };
  }

  return {
    position: "absolute",
    left,
    top,
    width,
    height,
    maxWidth: "100vw",
    maxHeight: "100vh",
  };
};

const getNodeColor = (widget: DonateWidget, item: PublicGridItem) => {
  const settings = getMergedSettings(widget, item);
  const styles = getMergedStyles(widget, item);
  return (
    styles.color ||
    styles.accentColor ||
    settings.accentColor ||
    settings.color ||
    DEFAULT_ACCENT_COLOR
  );
};

const readAlertEvent = (
  widget: DonateWidget,
  item: PublicGridItem,
  searchParams: URLSearchParams,
): AlertEvent | undefined => {
  const hasQueryEvent =
    searchParams.has("name") ||
    searchParams.has("donor") ||
    searchParams.has("donorName") ||
    searchParams.has("amount") ||
    searchParams.has("message");

  if (hasQueryEvent) {
    return {
      name: searchParams.get("name") || undefined,
      donorName: searchParams.get("donorName") || undefined,
      donor: searchParams.get("donor") || undefined,
      amount: searchParams.get("amount") || undefined,
      message: searchParams.get("message") || undefined,
      paymentMethod: searchParams.get("paymentMethod") || undefined,
    };
  }

  const settings = getMergedSettings(widget, item);
  const candidates = [
    settings.currentAlert,
    settings.latestAlert,
    settings.alert,
    widget.metadata?.currentAlert,
    widget.metadata?.latestAlert,
    widget.metadata?.alert,
  ];

  return candidates.find(
    (candidate) => candidate && typeof candidate === "object",
  ) as AlertEvent | undefined;
};

const normalizeAlertEvent = (event: unknown): AlertEvent | undefined => {
  if (!event || typeof event !== "object") return undefined;

  const payload = event as DonationAlertEvent;
  const amount = payload.amount ?? payload.total ?? payload.value;

  return {
    ...payload,
    id: normalizeString(
      payload.id || payload._id || payload.donationId || payload.transactionId,
      "",
    ),
    transactionId: normalizeString(
      payload.transactionId || payload.id || payload._id || payload.donationId,
      "",
    ),
    donationId: normalizeString(
      payload.donationId || payload.id || payload._id || payload.transactionId,
      "",
    ),
    name: normalizeString(
      payload.name || payload.donorName || payload.donor,
      "",
    ),
    donorName: normalizeString(
      payload.donorName || payload.name || payload.donor,
      "",
    ),
    donor: normalizeString(
      payload.donor || payload.donorName || payload.name,
      "",
    ),
    amount,
    currency: normalizeString(payload.currency, "LAK"),
    message: normalizeString(payload.message, ""),
    notiAudioUrl: normalizeString(
      payload.notiAudioUrl || payload.notificationAudioUrl,
      "",
    ),
    ttsAudioUrl: normalizeString(payload.ttsAudioUrl, ""),
  };
};

const parseAlertEventData = (data: string): AlertEvent | undefined => {
  try {
    return normalizeAlertEvent(JSON.parse(data));
  } catch {
    return undefined;
  }
};

const getAlertEventKey = (event?: AlertEvent) => {
  if (!event) return "";

  const id = event.id || event.transactionId || event.donationId || event._id;
  if (id) {
    const replayMarker =
      event.lastDisplayedAt || event.displayCount || event._receivedAt;

    return replayMarker ? `${id}|${replayMarker}` : String(id);
  }

  return [
    event.donorName || event.name || event.donor || "",
    event.amount ?? "",
    event.currency || "",
    event.message || "",
    event._receivedAt || "",
  ].join("|");
};

const getAlertDisplayMs = (settings: Record<string, any>) => {
  const displayMs = normalizeNumber(settings.displayMs, 0);
  if (displayMs > 0) return displayMs;

  const durationSeconds = normalizeNumber(settings.duration, 0);
  if (durationSeconds > 0) return durationSeconds * 1000;

  return 8000;
};

const shouldShowAlertEvent = (
  event: AlertEvent | undefined,
  settings: Record<string, any>,
) => {
  if (!event) return false;

  const minAmount = Math.max(normalizeNumber(settings.minAmount, 0), 0);
  if (minAmount <= 0) return true;
  if (
    event.amount === undefined ||
    event.amount === null ||
    event.amount === ""
  ) {
    return true;
  }

  return normalizeNumber(event.amount, 0) >= minAmount;
};

const formatAlertTemplate = ({
  template,
  donorName,
  amount,
  currency,
  message,
}: {
  template: string;
  donorName: string;
  amount: unknown;
  currency: string;
  message: string;
}) =>
  template
    .replace(/\{user\}/g, donorName)
    .replace(/\{name\}/g, donorName)
    .replace(/\{amount\}/g, formatCurrency(amount))
    .replace(/\{currency\}/g, currency)
    .replace(/\{message\}/g, message);

const getAlertAudioUrls = ({
  widget,
  settings,
  event,
}: {
  widget: DonateWidget;
  settings: Record<string, any>;
  event?: AlertEvent;
}) => ({
  notiAudioUrl: normalizeString(
    event?.notiAudioUrl ||
      settings.notiAudioUrl ||
      settings.notificationAudioUrl ||
      widget.notiAudioUrl ||
      widget.metadata?.notiAudioUrl ||
      widget.metadata?.notificationAudioUrl,
    "",
  ),
  ttsAudioUrl: normalizeString(
    event?.ttsAudioUrl ||
      settings.ttsAudioUrl ||
      widget.ttsAudioUrl ||
      widget.metadata?.ttsAudioUrl,
    "",
  ),
});

const getAudioVolume = (
  settings: Record<string, any>,
  key: string,
  fallback = 1,
) => {
  const rawValue = settings[key] ?? settings.volume;
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return fallback;
  }

  const value = normalizeNumber(rawValue, fallback);
  return value > 1 ? clampNumber(value / 100, 0, 1) : clampNumber(value, 0, 1);
};

const playAlertAudioUrl = (url: string, volume: number) => {
  if (!url || typeof window === "undefined") {
    return {
      done: Promise.resolve(),
      started: Promise.resolve(false),
      stop: () => undefined,
    };
  }

  let isDone = false;
  let isStopped = false;
  let activeAudio: HTMLAudioElement | undefined;
  let retryTimeoutId: number | undefined;
  let attemptTimeoutId: number | undefined;
  let cleanup: () => void = () => undefined;
  let completePlayback: () => void = () => undefined;
  let hasResolvedStarted = false;
  let resolveStarted: (value: boolean) => void = () => undefined;
  let stopSelf: () => void = () => undefined;

  const started = new Promise<boolean>((resolve) => {
    resolveStarted = (value) => {
      if (hasResolvedStarted) return;

      hasResolvedStarted = true;
      resolve(value);
    };
  });

  const done = new Promise<void>((resolve) => {
    const complete = () => {
      if (isDone) return;

      isDone = true;
      cleanup();
      resolveStarted(false);
      activeAlertAudioStops.delete(stopSelf);
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
        retryTimeoutId = undefined;
      }
      if (attemptTimeoutId) {
        window.clearTimeout(attemptTimeoutId);
        attemptTimeoutId = undefined;
      }
      resolve();
    };
    completePlayback = complete;

    const stopAudio = (audio: HTMLAudioElement) => {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    };

    const startAttempt = (attempt: number) => {
      if (isDone || isStopped) return;

      const audio = new Audio(url);
      activeAudio = audio;
      let isAttemptDone = false;
      let hasStartedPlaying = false;

      const cleanupAttempt = () => {
        audio.removeEventListener("ended", finish);
        audio.removeEventListener("canplay", playWhenReady);
        audio.removeEventListener("canplaythrough", playWhenReady);
        audio.removeEventListener("loadeddata", playWhenReady);
        audio.removeEventListener("error", retryOrComplete);
        if (attemptTimeoutId) {
          window.clearTimeout(attemptTimeoutId);
          attemptTimeoutId = undefined;
        }
      };

      const finish = () => {
        if (isAttemptDone) return;

        isAttemptDone = true;
        complete();
      };
      const retryOrComplete = () => {
        if (isAttemptDone) return;

        isAttemptDone = true;
        cleanupAttempt();
        stopAudio(audio);

        if (isDone || isStopped || attempt >= ALERT_AUDIO_RETRY_COUNT) {
          complete();
          return;
        }

        retryTimeoutId = window.setTimeout(() => {
          retryTimeoutId = undefined;
          startAttempt(attempt + 1);
        }, ALERT_AUDIO_RETRY_DELAY_MS);
      };
      const playWhenReady = () => {
        if (isAttemptDone || isStopped || hasStartedPlaying) return;

        hasStartedPlaying = true;

        try {
          const playPromise = audio.play();
          if (playPromise) {
            playPromise.then(() => resolveStarted(true)).catch(retryOrComplete);
          } else {
            resolveStarted(true);
          }
        } catch {
          retryOrComplete();
        }
      };

      cleanup = cleanupAttempt;

      audio.preload = "auto";
      audio.volume = clampNumber(volume, 0, 1);
      audio.addEventListener("ended", finish, { once: true });
      audio.addEventListener("canplay", playWhenReady, { once: true });
      audio.addEventListener("canplaythrough", playWhenReady, { once: true });
      audio.addEventListener("loadeddata", playWhenReady, { once: true });
      audio.addEventListener("error", retryOrComplete, { once: true });

      attemptTimeoutId = window.setTimeout(
        retryOrComplete,
        ALERT_AUDIO_ATTEMPT_TIMEOUT_MS,
      );

      audio.load();

      if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        playWhenReady();
      }
    };

    startAttempt(0);
  });

  stopSelf = () => {
    isStopped = true;
    cleanup();
    activeAlertAudioStops.delete(stopSelf);
    if (retryTimeoutId) {
      window.clearTimeout(retryTimeoutId);
      retryTimeoutId = undefined;
    }
    if (attemptTimeoutId) {
      window.clearTimeout(attemptTimeoutId);
      attemptTimeoutId = undefined;
    }
    if (activeAudio) {
      activeAudio.pause();
      activeAudio.removeAttribute("src");
      activeAudio.load();
    }
    completePlayback();
  };

  stopActiveAlertAudio();
  activeAlertAudioStops.add(stopSelf);

  return {
    done,
    started,
    stop: stopSelf,
  };
};

const shouldPreview = (searchParams: URLSearchParams) =>
  searchParams.get("preview") === "1" ||
  searchParams.get("preview") === "true" ||
  searchParams.get("test") === "1";

const makeGoalWidgetFromQuery = (
  searchParams: URLSearchParams,
): DonateWidget | undefined => {
  const hasGoalQuery =
    searchParams.has("title") ||
    searchParams.has("current") ||
    searchParams.has("target");

  if (!hasGoalQuery) return undefined;

  return {
    name: "Donation Goal",
    type: "DONATION_GOAL",
    status: "ACTIVE",
    metadata: {
      widgetKey: "goal",
      source: "query",
    },
    gridLayout: {
      layouts: {
        lg: [{ i: "default", x: 9, y: 2, w: 5, h: 2 }],
        md: [{ i: "default", x: 7, y: 2, w: 5, h: 2 }],
        sm: [{ i: "default", x: 3, y: 2, w: 5, h: 2 }],
        xs: [{ i: "default", x: 2, y: 2, w: 4, h: 2 }],
        xxs: [{ i: "default", x: 0, y: 2, w: 4, h: 2 }],
      },
      items: [
        {
          id: "default",
          type: "DONATION_GOAL",
          title: searchParams.get("title") || "Stream goal",
          data: { kind: "goal" },
          settings: {
            title: searchParams.get("title") || "Stream goal",
            current: normalizeNumber(searchParams.get("current"), 0),
            target: normalizeNumber(searchParams.get("target"), 0),
            showPercent: true,
            currency: "LAK",
          },
          styles: {
            color: searchParams.get("color") || "#168FEB",
          },
        },
      ],
      breakpoints: DEFAULT_BREAKPOINTS,
      cols: DEFAULT_COLS,
      rowHeight: DEFAULT_ROW_HEIGHT,
      margin: DEFAULT_MARGIN,
      containerPadding: DEFAULT_CONTAINER_PADDING,
      compactType: "vertical",
      preventCollision: false,
      isDraggable: false,
      isResizable: false,
    },
  };
};

const makeAlertWidgetFromQuery = (
  searchParams: URLSearchParams,
  publicKey: string,
): DonateWidget => {
  const theme = normalizeString(searchParams.get("theme"), "sticker");
  const color = normalizeColor(searchParams.get("color"), DEFAULT_ACCENT_COLOR);
  const displayMs = normalizeNumber(searchParams.get("displayMs"), 8000);
  const duration = normalizeNumber(searchParams.get("duration"), 0);

  return {
    name: "Donation Alert",
    type: "DONATION_ALERT",
    status: "ACTIVE",
    publicKey,
    metadata: {
      widgetKey: "alert",
      source: "donation-alert-route",
    },
    settings: {
      theme,
      displayMs: displayMs > 0 ? displayMs : undefined,
      duration: duration > 0 ? duration : undefined,
      minAmount: normalizeNumber(searchParams.get("minAmount"), 0),
      messageTemplate:
        searchParams.get("template") ||
        searchParams.get("messageTemplate") ||
        "{user} ໂດເນດ {amount} {currency}",
      imageUrl: searchParams.get("imageUrl") || DEFAULT_WIDGET_IMAGE,
      iconMode: searchParams.get("iconMode") || "image",
      iconUrl:
        searchParams.get("iconUrl") ||
        searchParams.get("imageUrl") ||
        DEFAULT_WIDGET_IMAGE,
      iconEmoji: searchParams.get("iconEmoji") || "💜",
      iconShape: searchParams.get("iconShape") || "none",
      iconSize: normalizeNumber(searchParams.get("iconSize"), 84),
      animation: searchParams.get("animation") || "pop",
      effect: searchParams.get("effect") || "none",
      backgroundStyle: searchParams.get("backgroundStyle") || "none",
      currency: searchParams.get("currency") || "LAK",
      anchor: normalizeWidgetAnchor(searchParams.get("anchor") || "center"),
      notiAudioUrl:
        searchParams.get("notiAudioUrl") ||
        searchParams.get("notificationAudioUrl") ||
        "",
      ttsAudioUrl: searchParams.get("ttsAudioUrl") || "",
      showDonorName: searchParams.get("showDonorName") !== "false",
      showAmount: searchParams.get("showAmount") !== "false",
      showMessage: searchParams.get("showMessage") !== "false",
      showBorderGlow: true,
    },
    styles: {
      color,
      textColor: searchParams.get("textColor") || "#FFFFFF",
    },
  };
};

function useTransparentPageBackground() {
  useEffect(() => {
    const htmlBackground = document.documentElement.style.background;
    const bodyBackground = document.body.style.background;
    const root = document.getElementById("root");
    const rootBackground = root?.style.background;

    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    if (root) root.style.background = "transparent";

    return () => {
      document.documentElement.style.background = htmlBackground;
      document.body.style.background = bodyBackground;
      if (root) root.style.background = rootBackground || "";
    };
  }, []);
}

function useViewportSize() {
  const [size, setSize] = useState(() => ({
    width: typeof window === "undefined" ? 1920 : window.innerWidth || 1920,
    height: typeof window === "undefined" ? 1080 : window.innerHeight || 1080,
  }));

  useEffect(() => {
    const updateSize = () => {
      setSize({
        width: window.innerWidth || 1920,
        height: window.innerHeight || 1080,
      });
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  return size;
}

const getTestViewportOverride = (searchParams: URLSearchParams) => {
  const width = Math.round(normalizeNumber(searchParams.get("testWidth"), 0));
  const height = Math.round(normalizeNumber(searchParams.get("testHeight"), 0));

  if (width > 0 && height > 0) {
    return { width, height };
  }

  return undefined;
};

const getRenderedViewportSize = (
  viewport: { width: number; height: number },
  testMode: boolean,
  searchParams: URLSearchParams,
) => {
  const override = testMode ? getTestViewportOverride(searchParams) : undefined;
  return override || viewport;
};

function useTimerSeconds(durationMinutes: unknown) {
  const durationSeconds = Math.max(normalizeNumber(durationMinutes, 0), 0) * 60;
  const [seconds, setSeconds] = useState(durationSeconds);

  useEffect(() => {
    setSeconds(durationSeconds);
  }, [durationSeconds]);

  useEffect(() => {
    if (durationSeconds <= 0) return undefined;

    const intervalId = window.setInterval(() => {
      setSeconds((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [durationSeconds]);

  return seconds;
}

function useDonationAlertEvents({
  publicKey,
  eventsUrl,
  enabled,
  debug,
}: {
  publicKey: string;
  eventsUrl?: string;
  enabled: boolean;
  debug: boolean;
}) {
  const [event, setEvent] = useState<AlertEvent | undefined>(undefined);
  const [eventKey, setEventKey] = useState("");
  const [status, setStatus] = useState<
    "idle" | "connecting" | "connected" | "error"
  >("idle");
  const connectionKeyRef = useRef("");

  useEffect(() => {
    if (
      !enabled ||
      !publicKey ||
      typeof window === "undefined" ||
      typeof window.EventSource === "undefined"
    ) {
      setStatus("idle");
      setEvent(undefined);
      setEventKey("");
      return undefined;
    }

    const resolvedEventsUrl = normalizeDonateApiUrl(
      normalizeString(eventsUrl, "") ||
        getDonationAlertEventsUrl({ publicKey }),
    );
    const connectionKey = `${publicKey}:${Date.now()}`;
    connectionKeyRef.current = connectionKey;
    setStatus("connecting");

    const source = new window.EventSource(resolvedEventsUrl);

    const handleConnected = () => {
      if (connectionKeyRef.current === connectionKey) {
        setStatus("connected");
      }
    };

    const handleDonation = (message: MessageEvent) => {
      const parsedEvent = parseAlertEventData(message.data);
      if (!parsedEvent) return;

      const nextEvent = normalizeAlertEvent({
        ...parsedEvent,
        _receivedAt: Date.now(),
      });
      if (!nextEvent) return;

      const nextKey = getAlertEventKey(nextEvent);
      if (connectionKeyRef.current !== connectionKey) return;

      setStatus("connected");
      setEvent(nextEvent);
      setEventKey(nextKey);
    };

    source.addEventListener("connected", handleConnected as EventListener);
    source.addEventListener("donation", handleDonation as EventListener);
    source.onerror = (error) => {
      if (debug) {
        console.error("donation alert event stream error", error);
      }

      if (connectionKeyRef.current === connectionKey) {
        setStatus("error");
      }
    };

    return () => {
      source.removeEventListener("connected", handleConnected as EventListener);
      source.removeEventListener("donation", handleDonation as EventListener);
      source.close();
    };
  }, [debug, enabled, eventsUrl, publicKey]);

  return { event, eventKey, status };
}

function WidgetStatus({ message }: { message: string }) {
  return (
    <main className="fixed inset-0 grid place-items-center bg-transparent p-4 font-sans text-white">
      <div className="max-w-[420px] rounded-lg bg-black/75 px-4 py-3 text-center text-sm font-bold shadow-lg">
        {message}
      </div>
    </main>
  );
}

function DonationWidgetPublicPage() {
  const params = useParams<{ publicKey?: string; widgetType?: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const viewport = useViewportSize();
  useTransparentPageBackground();

  const publicKey =
    params.publicKey ||
    searchParams.get("publicKey") ||
    searchParams.get("key") ||
    "";
  const debug = searchParams.get("debug") === "1";
  const testMode = searchParams.get("test") === "1";
  const liveEventsEnabled = searchParams.get("live") !== "0";
  const pollEnabled =
    searchParams.get("poll") === "1" || searchParams.get("poll") === "true";
  const refreshMs = Math.max(
    normalizeNumber(searchParams.get("refreshMs"), 5000),
    1000,
  );
  const previewMode = shouldPreview(searchParams);
  const isDonationAlertRoute = params.widgetType === "alert";

  const {
    data: widgetResponse,
    isLoading,
    error,
  } = useQuery(
    ["donate-widget-public", publicKey],
    () => donateWidgetPublicGetOne({ publicKey }),
    {
      enabled: Boolean(publicKey),
      retry: false,
      refetchInterval: pollEnabled ? refreshMs : false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const queryGoalWidget = useMemo(
    () => makeGoalWidgetFromQuery(searchParams),
    [searchParams],
  );
  const queryAlertWidget = useMemo(
    () =>
      publicKey && isDonationAlertRoute
        ? makeAlertWidgetFromQuery(searchParams, publicKey)
        : undefined,
    [isDonationAlertRoute, publicKey, searchParams],
  );
  const fetchedWidget = publicKey
    ? normalizeWidgetResponse(widgetResponse)
    : undefined;
  const hasFetchedWidget = Boolean(
    fetchedWidget && (fetchedWidget.type || fetchedWidget.gridLayout),
  );
  const widget = publicKey
    ? hasFetchedWidget
      ? fetchedWidget
      : queryAlertWidget
    : queryGoalWidget;
  const hasWidget = Boolean(widget && (widget.type || widget.gridLayout));
  const hasAlertWidget = Boolean(
    widget &&
    getWidgetItems(widget).some(
      (item) => getItemKind(item, widget) === "alert",
    ),
  );
  const alertEventsUrl = normalizeString(
    widget?.donationAlertEventsUrl || widget?.metadata?.donationAlertEventsUrl,
    "",
  );
  const alertEvents = useDonationAlertEvents({
    publicKey,
    eventsUrl: alertEventsUrl,
    enabled: Boolean(
      publicKey &&
        liveEventsEnabled &&
        hasAlertWidget &&
        !previewMode &&
        !testMode,
    ),
    debug,
  });
  const [testAlertForm, setTestAlertForm] = useState<TestAlertFormState>({
    name: "ໂຈໂຈ້",
    amount: "25000",
    paymentMethod: "",
    message: "ຂໍໃຫ້ stream ມ່ວນໆ",
  });
  const [testAlertEvent, setTestAlertEvent] = useState<AlertEvent>();
  const [testAlertEventKey, setTestAlertEventKey] = useState("");

  if (!publicKey && !queryGoalWidget) {
    return <WidgetStatus message="Missing widget public key" />;
  }

  if (isLoading && debug && !hasWidget) {
    return <WidgetStatus message="Loading widget..." />;
  }

  if (error && debug && !hasWidget) {
    return <WidgetStatus message={donateErrorMessage(error)} />;
  }

  if (widget?.status === "INACTIVE" && !previewMode) {
    return null;
  }

  if (!hasWidget) {
    if (debug) return <WidgetStatus message="Widget not found" />;
    return null;
  }

  const activeWidget = widget as DonateWidget;
  const renderedViewport = getRenderedViewportSize(
    viewport,
    testMode,
    searchParams,
  );
  const breakpoint = getBreakpoint(
    renderedViewport.width,
    activeWidget.gridLayout?.breakpoints,
  );
  const items = getWidgetItems(activeWidget);

  return (
    <main
      className="fixed inset-0 bg-transparent font-sans"
      data-widget-public-key={publicKey}
      data-widget-path={location.pathname}
      data-widget-alert-stream={alertEvents.status}
      style={{
        width: renderedViewport.width,
        height: renderedViewport.height,
      }}
    >
      {items.map((item, index) => (
        <PositionedWidgetItem
          key={getItemId(item, index)}
          widget={activeWidget}
          items={items}
          item={item}
          index={index}
          breakpoint={breakpoint}
          viewportWidth={renderedViewport.width}
          viewportHeight={renderedViewport.height}
          searchParams={searchParams}
          previewMode={previewMode}
          liveAlert={testMode ? testAlertEvent : alertEvents.event}
          liveAlertKey={testMode ? testAlertEventKey : alertEvents.eventKey}
        />
      ))}
      {testMode && hasAlertWidget && (
        <WidgetPublicTestControls
          form={testAlertForm}
          onChange={(patch) =>
            setTestAlertForm((current) => ({ ...current, ...patch }))
          }
          onSend={() => {
            const now = Date.now();
            setTestAlertEvent({
              id: `test-${now}`,
              donationId: `test-${now}`,
              transactionId: `test-${now}`,
              name: testAlertForm.name,
              donorName: testAlertForm.name,
              donor: testAlertForm.name,
              amount: Number(testAlertForm.amount || 0),
              currency: "LAK",
              paymentMethod: testAlertForm.paymentMethod,
              message: testAlertForm.message,
              _receivedAt: now,
            });
            setTestAlertEventKey(`test-${now}`);
          }}
        />
      )}
    </main>
  );
}

function WidgetPublicTestControls({
  form,
  onChange,
  onSend,
}: {
  form: TestAlertFormState;
  onChange: (patch: Partial<TestAlertFormState>) => void;
  onSend: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="fixed bottom-4 right-4 z-[999999] inline-flex h-12 items-center gap-2 rounded-lg border-0 bg-[#168feb] px-4 text-sm font-black text-white shadow-[0_16px_34px_rgba(22,143,235,0.35)]"
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>▶</span>
        ທົດສອບ
      </button>

      {isOpen && (
        <aside className="fixed bottom-[76px] right-4 z-[999999] w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-lg border border-white/15 bg-white text-[#101828] shadow-[0_22px_60px_rgba(0,0,0,0.38)]">
          <div className="flex items-center justify-between gap-3 border-b border-[#dbe4ef] px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-black">
                ທົດສອບ Donation Alert
              </div>
              <div className="truncate text-xs font-bold text-[#667085]">
                ຈຳລອງ event ໃນໜ້ານີ້
              </div>
            </div>
            <button
              type="button"
              className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-[#dbe4ef] bg-white text-[#344054]"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="grid gap-3 p-3">
            <WidgetTestInput
              label="ຊື່ຜູ້ໂດເນດ"
              value={form.name}
              onChange={(value) => onChange({ name: value })}
            />
            <WidgetTestInput
              label="ຈຳນວນເງິນ"
              value={form.amount}
              type="number"
              suffix="LAK"
              onChange={(value) => onChange({ amount: value })}
            />
            <label className="block min-w-0">
              <span className="mb-1 block text-xs font-bold text-[#667085]">
                ຊ່ອງທາງຊຳລະ
              </span>
              <select
                value={form.paymentMethod}
                onChange={(event) =>
                  onChange({ paymentMethod: event.target.value })
                }
                className="h-10 w-full rounded-lg border border-[#dbe4ef] bg-[#f8fafc] px-3 text-sm font-bold text-[#344054] outline-none focus:border-[#168feb] focus:bg-white"
              >
                <option value="">Default payment</option>
                <option value="BCEL">BCEL</option>
                <option value="JDB">JDB</option>
                <option value="LDB">LDB</option>
                <option value="CASH">Cash</option>
              </select>
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block text-xs font-bold text-[#667085]">
                ຂໍ້ຄວາມ
              </span>
              <textarea
                value={form.message}
                onChange={(event) => onChange({ message: event.target.value })}
                className="min-h-[82px] w-full resize-none rounded-lg border border-[#dbe4ef] bg-[#f8fafc] px-3 py-2 text-sm font-bold text-[#344054] outline-none focus:border-[#168feb] focus:bg-white"
              />
            </label>
            <button
              type="button"
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border-0 bg-[#168feb] px-4 text-sm font-black text-white"
              onClick={onSend}
            >
              ທົດລອງສະແດງ Alert
            </button>
          </div>
        </aside>
      )}
    </>
  );
}

function WidgetTestInput({
  label,
  value,
  type = "text",
  suffix,
  onChange,
}: {
  label: string;
  value: string;
  type?: string;
  suffix?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs font-bold text-[#667085]">
        {label}
      </span>
      <span className="flex h-10 items-center rounded-lg border border-[#dbe4ef] bg-[#f8fafc] focus-within:border-[#168feb] focus-within:bg-white">
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-full min-w-0 flex-1 rounded-lg border-0 bg-transparent px-3 text-sm font-bold text-[#344054] outline-none"
        />
        {suffix && (
          <span className="flex h-full items-center border-l border-[#dbe4ef] px-3 text-xs font-black text-[#475467]">
            {suffix}
          </span>
        )}
      </span>
    </label>
  );
}

function PositionedWidgetItem({
  widget,
  items,
  item,
  index,
  breakpoint,
  viewportWidth,
  viewportHeight,
  searchParams,
  previewMode,
  liveAlert,
  liveAlertKey,
}: {
  widget: DonateWidget;
  items: PublicGridItem[];
  item: PublicGridItem;
  index: number;
  breakpoint: DonateWidgetGridBreakpoint;
  viewportWidth: number;
  viewportHeight: number;
  searchParams: URLSearchParams;
  previewMode: boolean;
  liveAlert?: AlertEvent;
  liveAlertKey?: string;
}) {
  const config = getGridConfig(widget, breakpoint, viewportWidth);
  const layout = getItemLayout({
    widget,
    items,
    item,
    index,
    breakpoint,
    cols: config.cols,
    viewportWidth,
    viewportHeight,
  });
  const pixels = getLayoutPixels({
    layout,
    widget,
    breakpoint,
    viewportWidth,
    viewportHeight,
  });
  const anchor = getItemAnchor(widget, item);
  const inferredBottomLeft =
    anchor === "free" &&
    isBottomLeftLayout({
      layout,
      widget,
      breakpoint,
      viewportWidth,
      viewportHeight,
    });

  return (
    <div
      className="donation-widget-position"
      style={getPositionedWidgetStyle({
        anchor,
        inferredBottomLeft,
        pixels,
        widget,
        breakpoint,
        viewportWidth,
        viewportHeight,
      })}
    >
      <PublicWidgetNode
        widget={widget}
        item={item}
        searchParams={searchParams}
        previewMode={previewMode}
        liveAlert={liveAlert}
        liveAlertKey={liveAlertKey}
      />
    </div>
  );
}

export function PublicWidgetNode({
  widget,
  item,
  searchParams,
  previewMode,
  liveAlert,
  liveAlertKey,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
  searchParams: URLSearchParams;
  previewMode: boolean;
  liveAlert?: AlertEvent;
  liveAlertKey?: string;
}) {
  const kind = getItemKind(item, widget);

  if (kind === "alert") {
    return (
      <DonationAlertNode
        widget={widget}
        item={item}
        searchParams={searchParams}
        previewMode={previewMode}
        liveAlert={liveAlert}
        liveAlertKey={liveAlertKey}
      />
    );
  }

  if (kind === "goal") {
    return <DonationGoalNode widget={widget} item={item} />;
  }

  if (kind === "leaderboard") {
    return (
      <DonationLeaderboardNode
        widget={widget}
        item={item}
        previewMode={previewMode}
      />
    );
  }

  if (kind === "timer") {
    return <DonationTimerNode widget={widget} item={item} />;
  }

  if (kind === "chat") {
    return (
      <DonationChatNode widget={widget} item={item} previewMode={previewMode} />
    );
  }

  if (kind === "image") {
    return <DonationImageNode widget={widget} item={item} />;
  }

  if (kind === "amount") {
    return <DonationAmountNode widget={widget} item={item} />;
  }

  return <DonationTextNode widget={widget} item={item} />;
}

function DonationAlertNode({
  widget,
  item,
  searchParams,
  previewMode,
  liveAlert,
  liveAlertKey,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
  searchParams: URLSearchParams;
  previewMode: boolean;
  liveAlert?: AlertEvent;
  liveAlertKey?: string;
}) {
  const settings = getMergedSettings(widget, item);
  const styles = getMergedStyles(widget, item);
  const incomingEvent = liveAlert || readAlertEvent(widget, item, searchParams);
  const incomingEventKey = liveAlertKey || getAlertEventKey(incomingEvent);
  const alwaysVisible = normalizeBoolean(settings.alwaysVisible, false);
  const displayMs = getAlertDisplayMs(settings);
  const eventMeetsMinAmount = shouldShowAlertEvent(incomingEvent, settings);

  const [visibleEvent, setVisibleEvent] = useState<AlertEvent | undefined>(
    incomingEvent,
  );
  const [dismissedEventKey, setDismissedEventKey] = useState("");
  const [audioReadyEventKey, setAudioReadyEventKey] = useState("");
  const [audioFailedEventKey, setAudioFailedEventKey] = useState("");
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const alertFrameRef = useRef<HTMLDivElement | null>(null);
  const [alertScale, setAlertScale] = useState(1);

  const playbackSessionRef = useRef(0);

  const color = getNodeColor(widget, item);
  const textColor = normalizeString(
    settings.textColor || styles.textColor,
    "#FFFFFF",
  );

  const audioContextRef = useRef<AudioContext | null>(null);

  const unlockAudio = async () => {
    try {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;

      if (!AudioContextClass) {
        setAudioUnlocked(true);
        return;
      }

      const ctx = audioContextRef.current || new AudioContextClass();
      audioContextRef.current = ctx;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      gain.gain.value = 0.00001;

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(0);
      oscillator.stop(ctx.currentTime + 0.03);

      setAudioUnlocked(true);
    } catch (err) {
      console.warn("Unlock audio failed:", err);

      // ສຳຄັນ: ໃຫ້ປຸ່ມຫາຍໄປກ່ອນ ເຖິງແມ່ນ unlock fail
      // ເພາະບາງ browser/OBS ອະນຸຍາດສຽງຫຼັງ interaction ຢູ່ແລ້ວ
      setAudioUnlocked(true);
    }
  };

  useEffect(() => {
    if (!incomingEvent || !incomingEventKey || !eventMeetsMinAmount) {
      if (!previewMode && !alwaysVisible) {
        setVisibleEvent(undefined);
      }

      setAudioReadyEventKey("");
      setAudioFailedEventKey("");
      return undefined;
    }

    setDismissedEventKey("");
    setVisibleEvent(incomingEvent);
    setAudioReadyEventKey("");
    setAudioFailedEventKey("");
    return undefined;
  }, [
    alwaysVisible,
    eventMeetsMinAmount,
    incomingEvent,
    incomingEventKey,
    previewMode,
  ]);

  const visibleEventKey = getAlertEventKey(visibleEvent);

  const event =
    incomingEvent &&
    eventMeetsMinAmount &&
    incomingEventKey !== dismissedEventKey
      ? incomingEvent
      : visibleEvent && visibleEventKey !== dismissedEventKey
        ? visibleEvent
        : undefined;

  const activeEventKey = getAlertEventKey(event);

  const { notiAudioUrl, ttsAudioUrl } = getAlertAudioUrls({
    widget,
    settings,
    event,
  });

  const notiVolume = getAudioVolume(
    settings,
    settings.notiVolume !== undefined ? "notiVolume" : "notificationVolume",
    1,
  );

  const ttsVolume = getAudioVolume(settings, "ttsVolume", 1);

  const audioPlaybackKey = activeEventKey
    ? [activeEventKey, notiAudioUrl, ttsAudioUrl].join("|")
    : "";

  const hasAlertAudio = Boolean(audioUnlocked && (notiAudioUrl || ttsAudioUrl));
  const audioRequired = Boolean(event && !previewMode && audioUnlocked);

  // ສຳຄັນ: ຢ່າລໍຖ້າ audioReady ກ່ອນຈຶ່ງສະແດງ alert
  // ເພາະ browser ອາດ block ສຽງ ແລ້ວເຮັດໃຫ້ພາບບໍ່ຂຶ້ນ
  const showLiveAlert =
    Boolean(event) && audioFailedEventKey !== audioPlaybackKey;

  const showAlert = previewMode || (event ? showLiveAlert : alwaysVisible);

  useEffect(() => {
    if (
      !activeEventKey ||
      !audioPlaybackKey ||
      !audioRequired ||
      previewMode ||
      !hasAlertAudio
    ) {
      return undefined;
    }

    let isStopped = false;
    let currentPlayback: ReturnType<typeof playAlertAudioUrl> | undefined;
    let startedAnyAudio = false;

    const playbackSession = playbackSessionRef.current + 1;
    playbackSessionRef.current = playbackSession;

    const isActivePlaybackSession = () =>
      !isStopped && playbackSessionRef.current === playbackSession;

    const playOne = async (url: string, volume: number) => {
      try {
        currentPlayback = playAlertAudioUrl(url, volume);

        const didStart = await Promise.race([
          currentPlayback.started,
          currentPlayback.done.then(() => false),
        ]);

        if (didStart && isActivePlaybackSession() && !startedAnyAudio) {
          startedAnyAudio = true;
          setAudioReadyEventKey(audioPlaybackKey);
        }

        await currentPlayback.done;
      } catch (err) {
        console.warn("Alert audio play failed:", err);
      }
    };

    const playSequence = async () => {
      if (notiAudioUrl) {
        await playOne(notiAudioUrl, notiVolume);
      }

      if (isStopped || !ttsAudioUrl) return;

      await playOne(ttsAudioUrl, ttsVolume);

      if (isActivePlaybackSession() && !startedAnyAudio) {
        setAudioFailedEventKey(audioPlaybackKey);
      }
    };

    playSequence();

    return () => {
      isStopped = true;
      playbackSessionRef.current += 1;
      currentPlayback?.stop();
    };
  }, [
    activeEventKey,
    audioPlaybackKey,
    audioRequired,
    hasAlertAudio,
    notiAudioUrl,
    notiVolume,
    previewMode,
    ttsAudioUrl,
    ttsVolume,
  ]);

  useEffect(() => {
    if (
      !showLiveAlert ||
      !activeEventKey ||
      previewMode ||
      alwaysVisible ||
      displayMs <= 0
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDismissedEventKey(activeEventKey);
      setVisibleEvent(undefined);
    }, displayMs);

    return () => window.clearTimeout(timeoutId);
  }, [activeEventKey, alwaysVisible, displayMs, previewMode, showLiveAlert]);

  const donorName =
    event?.donorName ||
    event?.name ||
    event?.donor ||
    normalizeString(settings.name, "Supporter");

  const amount = event?.amount ?? settings.amount ?? 25000;
  const currency = normalizeString(event?.currency || settings.currency, "LAK");
  const message = event?.message || normalizeString(settings.message, "");
  const imageUrl = normalizeString(settings.imageUrl, DEFAULT_WIDGET_IMAGE);
  const iconMode = normalizeChoice(
    settings.iconMode || settings.iconType,
    ["image", "emoji", "none"],
    "image",
  );
  const iconUrl = normalizeString(
    settings.iconUrl || settings.iconImageUrl || imageUrl,
    DEFAULT_WIDGET_IMAGE,
  );
  const iconEmoji = normalizeString(settings.iconEmoji || settings.emoji, "💜");
  const iconShape = normalizeChoice(
    settings.iconShape,
    ["none", "circle", "rounded", "square"],
    "none",
  );
  const theme = normalizeChoice(
    settings.theme,
    ["sticker", "minimal", "marquee", "banner", "compact"],
    "sticker",
  );
  const layoutPreset = normalizeAlertLayoutPreset(
    settings.layoutPreset || settings.layout,
    theme,
  );
  const iconSize = clampNumber(normalizeNumber(settings.iconSize, 84), 32, 180);
  const showIcon =
    iconMode !== "none" &&
    normalizeBoolean(settings.showIcon, layoutPreset !== "text-only");
  const showDonorName = normalizeBoolean(settings.showDonorName, true);
  const showAmount = normalizeBoolean(settings.showAmount, true);
  const showMessage = normalizeBoolean(settings.showMessage, true);
  const alertBaseSize = useMemo(
    () => getAlertLayoutBaseSize(layoutPreset, showIcon),
    [layoutPreset, showIcon],
  );

  useEffect(() => {
    const element = alertFrameRef.current;
    if (!element || !showAlert) return undefined;

    const updateScale = () => {
      const rect = element.getBoundingClientRect();
      const framePadding = layoutPreset === "compact" ? 8 : 16;
      const availableWidth = Math.max(rect.width - framePadding, 1);
      const availableHeight = Math.max(rect.height - framePadding, 1);
      const nextScale = clampNumber(
        Math.min(
          availableWidth / alertBaseSize.width,
          availableHeight / alertBaseSize.height,
        ),
        0.2,
        4,
      );

      setAlertScale((currentScale) =>
        Math.abs(currentScale - nextScale) > 0.01 ? nextScale : currentScale,
      );
    };

    updateScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScale);
      return () => window.removeEventListener("resize", updateScale);
    }

    const observer = new ResizeObserver(updateScale);
    observer.observe(element);

    return () => observer.disconnect();
  }, [
    alertBaseSize.height,
    alertBaseSize.width,
    layoutPreset,
    showAlert,
  ]);

  if (!showAlert && !(!previewMode && !audioUnlocked)) return null;

  const template = formatAlertTemplate({
    template: normalizeString(
      settings.messageTemplate || item.data?.template || item.data?.text,
      "{user} donated {amount} {currency}",
    ),
    donorName,
    amount,
    currency,
    message,
  });
  const backgroundStyle = "none";
  const animationClass = getAnimationClass(settings.animation);

  const border = normalizeBoolean(settings.showBorderGlow, true)
    ? `2px solid ${color}`
    : "1px solid rgba(255,255,255,0.35)";

  const formattedAmount = `${formatCurrency(amount)} ${currency}`;
  const renderKey =
    getAlertEventKey(event) || (previewMode ? "preview" : "alert");
  const alertClassName = [
    animationClass,
    `donation-alert-bg-${backgroundStyle}`,
    !showIcon ? "donation-alert-no-icon" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const alertSurfaceStyle = getWidgetNodeStyle(color, {
    border,
    color: textColor,
  });
  const renderAlertIcon = (className = "") => {
    if (!showIcon) return null;

    return (
      <div
        className={`donation-alert-icon donation-alert-icon-${iconShape} ${className}`}
        style={{
          width: iconSize,
          height: iconSize,
          fontSize: Math.max(Math.round(iconSize * 0.5), 18),
          border,
        }}
      >
        {iconMode === "emoji" ? (
          <span>{iconEmoji}</span>
        ) : (
          <img src={iconUrl} alt="" />
        )}
      </div>
    );
  };

  const renderAmountPill = () =>
    showAmount ? (
      <span className="donation-widget-amount-pill">{formattedAmount}</span>
    ) : null;

  const renderAlertTitle = () =>
    showDonorName ? (
      <h2 className="donation-alert-title" style={{ color: textColor }}>
        {donorName}
      </h2>
    ) : null;

  const renderAlertMessage = () =>
    showMessage && message ? (
      <div className="donation-alert-message line-clamp-2">{message}</div>
    ) : null;

  const renderAlertTextStack = (label = "") => (
    <div className="donation-alert-text-stack">
      {label && <div className="donation-widget-label">{label}</div>}
      {renderAlertTitle()}
      {showAmount && (
        <div className="donation-alert-amount-row">{renderAmountPill()}</div>
      )}
      {renderAlertMessage()}
    </div>
  );

  const renderAlertLayoutContent = () => {
    if (layoutPreset === "text-only") {
      return <div className="donation-alert-template-text">{template}</div>;
    }

    if (layoutPreset === "compact") {
      return (
        <>
          {renderAlertIcon("donation-alert-icon-inline")}
          <div className="donation-alert-body">
            {showDonorName && (
              <strong className="truncate" style={{ color: textColor }}>
                {donorName}
              </strong>
            )}
            {renderAmountPill()}
          </div>
        </>
      );
    }

    if (layoutPreset === "icon-left") {
      return (
        <>
          {renderAlertIcon("donation-alert-side-icon")}
          {renderAlertTextStack("Donate")}
        </>
      );
    }

    if (layoutPreset === "icon-right") {
      return (
        <>
          {renderAlertTextStack("Donation Alert")}
          {renderAlertIcon("donation-alert-side-icon")}
        </>
      );
    }

    if (layoutPreset === "banner") {
      return (
        <>
          {renderAlertIcon("donation-alert-side-icon")}
          {renderAlertTextStack("Donation Alert")}
        </>
      );
    }

    return (
      <>
        {renderAlertIcon("donation-alert-avatar")}
        {renderAlertTextStack(
          layoutPreset === "center" ? "Donation Alert" : "",
        )}
      </>
    );
  };

  const alertShellStyle = getWidgetNodeStyle(color, {
    color: textColor,
    "--donation-alert-scale": alertScale,
    "--donation-alert-base-width": `${alertBaseSize.width}px`,
    "--donation-alert-base-height": `${alertBaseSize.height}px`,
  } as React.CSSProperties);

  return (
    <>
      {!previewMode && !audioUnlocked && (
        <button
          type="button"
          onPointerDown={unlockAudio}
          onClick={unlockAudio}
          style={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 999999,
            width: 260,
            minHeight: 64,
            padding: "14px 20px",
            borderRadius: 18,
            border: "1px solid rgba(255,255,255,0.28)",
            background:
              "linear-gradient(135deg, rgba(17,24,39,0.96), rgba(245,158,11,0.95))",
            color: "#FFFFFF",
            fontSize: 18,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          }}
        >
          ກົດເພື່ອເປີດສຽງ
        </button>
      )}

      {showAlert && (
        <div
          ref={alertFrameRef}
          key={renderKey}
          className={`donation-widget-node donation-alert-scale-shell donation-alert-layout-${layoutPreset} ${alertClassName}`}
          style={alertShellStyle}
        >
          <div className="donation-alert-scale-content">
            <div className="donation-alert-content" style={alertSurfaceStyle}>
              {renderAlertLayoutContent()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DonationGoalNode({
  widget,
  item,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
}) {
  const settings = getMergedSettings(widget, item);
  const color = getNodeColor(widget, item);
  const current = normalizeNumber(settings.current, 0);
  const target = normalizeNumber(settings.target, 0);
  const percent =
    target > 0 ? Math.min(Math.max((current / target) * 100, 0), 100) : 0;
  const title = normalizeString(settings.title || item.title, "Stream goal");
  const showPercent = normalizeBoolean(settings.showPercent, true);
  const layout = normalizeString(settings.layout, "card");

  if (layout === "bar") {
    return (
      <div
        className="donation-widget-node donation-widget-card donation-widget-pop"
        style={getWidgetNodeStyle(color)}
      >
        <div className="donation-goal-content">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="donation-goal-title min-w-0 truncate text-sm">
              {title}
            </span>
            <span className="flex-none text-[#101828]">
              {formatCurrency(current)} / {formatCurrency(target)}
            </span>
          </div>
          <div className="donation-goal-progress">
            <span style={{ width: `${percent}%` }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="donation-widget-node donation-widget-card donation-widget-pop"
      style={getWidgetNodeStyle(color)}
    >
      <div className="donation-goal-content">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="donation-widget-label mb-2 max-w-full">Goal</div>
            <div className="donation-goal-title truncate text-lg leading-tight">
              {title}
            </div>
          </div>
          {showPercent && (
            <span className="donation-widget-amount-pill flex-none">
              {Math.round(percent)}%
            </span>
          )}
        </div>
        <div className="mb-3 flex items-end justify-between gap-3">
          <strong className="truncate text-xl font-black text-[#101828]">
            {formatCurrency(current)}
          </strong>
          <span className="flex-none text-sm font-bold text-[#667085]">
            / {formatCurrency(target)} LAK
          </span>
        </div>
        <div className="donation-goal-progress">
          <span style={{ width: `${percent}%` }} />
        </div>
      </div>
    </div>
  );
}

function DonationLeaderboardNode({
  widget,
  item,
  previewMode,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
  previewMode: boolean;
}) {
  const settings = getMergedSettings(widget, item);
  const color = getNodeColor(widget, item);
  const rows = Array.isArray(settings.rows)
    ? settings.rows
    : Array.isArray(settings.donations)
      ? settings.donations
      : previewMode
        ? previewLeaderboardRows
        : [];
  const limit = Math.max(normalizeNumber(settings.limit, 5), 1);

  return (
    <div
      className="donation-widget-node donation-widget-card donation-widget-pop p-4"
      style={getWidgetNodeStyle(color)}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="donation-widget-label mb-2 max-w-full">
            Top Donors
          </div>
          <div className="donation-leaderboard-title truncate text-lg leading-tight">
            {normalizeString(
              settings.title || item.title,
              "Donation leaderboard",
            )}
          </div>
        </div>
      </div>
      <div className="donation-leaderboard-list">
        {rows.slice(0, limit).map((row: any, index: number) => {
          const name = normalizeString(
            row.name || row.donorName || row.donor,
            "Supporter",
          );
          const amount = row.amount || row.total || 0;

          return (
            <div key={`${name}-${index}`} className="donation-leaderboard-row">
              <span className="donation-rank">{index + 1}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-black text-[#344054]">
                {name}
              </span>
              {settings.showAmounts !== false && (
                <span className="donation-widget-amount-pill text-xs">
                  {formatCurrency(amount)} LAK
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DonationTimerNode({
  widget,
  item,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
}) {
  const settings = getMergedSettings(widget, item);
  const seconds = useTimerSeconds(settings.durationMinutes);
  const color = getNodeColor(widget, item);
  const opacity =
    Math.max(Math.min(normalizeNumber(settings.opacity, 100), 100), 0) / 100;
  const scale = Math.max(normalizeNumber(settings.scale, 100), 1) / 100;

  return (
    <div
      className="donation-widget-node donation-widget-card donation-widget-dark donation-timer donation-widget-pop"
      style={getWidgetNodeStyle(color, {
        opacity,
        transform: `scale(${scale})`,
        transformOrigin: "center",
      })}
    >
      <div className="text-center">
        <div className="donation-widget-label mb-3 max-w-full bg-white/10 text-white">
          {normalizeString(settings.status, "Live")}
        </div>
        <div className="donation-timer-time">{formatTimer(seconds)}</div>
        <div className="mt-2 text-sm font-bold text-white/70">
          {normalizeString(settings.label, "Stream timer")}
        </div>
      </div>
    </div>
  );
}

function DonationChatNode({
  widget,
  item,
  previewMode,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
  previewMode: boolean;
}) {
  const settings = getMergedSettings(widget, item);
  const color = getNodeColor(widget, item);
  const isDark = settings.theme === "dark";
  const rows = Array.isArray(settings.messages)
    ? settings.messages
    : Array.isArray(settings.rows)
      ? settings.rows
      : previewMode
        ? previewChatRows
        : [];

  return (
    <div
      className={`donation-widget-node donation-widget-card donation-widget-pop p-3 ${
        isDark ? "donation-widget-dark" : ""
      }`}
      style={getWidgetNodeStyle(color, {
        fontSize: `${normalizeNumber(settings.fontSize, 14)}px`,
      })}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div
            className={`donation-widget-label mb-2 max-w-full ${
              isDark ? "bg-white/10 text-white" : ""
            }`}
          >
            Chat
          </div>
          <div
            className={`donation-chat-title truncate text-lg leading-tight ${
              isDark ? "text-white" : ""
            }`}
          >
            {normalizeString(settings.title || item.title, "Live chat")}
          </div>
        </div>
      </div>
      <div className="donation-chat-list">
        {rows.map((row: any, index: number) => {
          const user = normalizeString(row.user || row.name, "User");
          const message = normalizeString(row.message || row.text, "");

          return (
            <div key={`${user}-${index}`} className="donation-chat-message">
              {settings.showAvatar !== false && (
                <span
                  className="grid h-7 w-7 flex-none place-items-center rounded-full text-[10px] font-black text-white"
                  style={{ backgroundColor: row.color || "#168FEB" }}
                >
                  {user.slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className="donation-chat-bubble">
                <strong>{user}: </strong>
                <span>{message}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DonationImageNode({
  widget,
  item,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
}) {
  const settings = getMergedSettings(widget, item);
  const imageUrl = normalizeString(
    settings.imageUrl || item.data?.imageUrl,
    "",
  );
  if (!imageUrl) return null;

  return (
    <div className="donation-widget-node donation-widget-pop">
      <img
        src={imageUrl}
        alt=""
        className="h-full w-full rounded-lg object-cover shadow-[0_18px_42px_rgba(15,23,42,0.18)]"
        style={{ objectFit: normalizeString(settings.fit, "cover") as any }}
      />
    </div>
  );
}

function DonationAmountNode({
  widget,
  item,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
}) {
  const settings = getMergedSettings(widget, item);
  const color = getNodeColor(widget, item);

  return (
    <div
      className="donation-widget-node donation-widget-card donation-amount donation-widget-pop"
      style={getWidgetNodeStyle(color)}
    >
      <div className="min-w-0">
        <div className="donation-widget-label mb-2 max-w-full">Donation</div>
        <div className="donation-amount-value">
          {formatCurrency(settings.amount || item.data?.text || 0)} LAK
        </div>
      </div>
    </div>
  );
}

function DonationTextNode({
  widget,
  item,
}: {
  widget: DonateWidget;
  item: PublicGridItem;
}) {
  const settings = getMergedSettings(widget, item);
  const color = getNodeColor(widget, item);
  const text = normalizeString(
    item.data?.text || settings.text || item.title || widget.name,
    "",
  );

  if (!text) return null;

  return (
    <div
      className="donation-widget-node donation-widget-card donation-widget-pop flex items-center px-4 py-3"
      style={getWidgetNodeStyle(color, {
        color,
        fontSize: normalizeNumber(settings.fontSize, 18),
        fontWeight: normalizeString(settings.fontWeight, "bold") as any,
      })}
    >
      <span className="line-clamp-3">{text}</span>
    </div>
  );
}

export default DonationWidgetPublicPage;
